// Local, per-device UI preferences that must apply before the user's settings load (theme, motion).
// Server settings (GET /users/@me/settings) overwrite them once signed in.
import { create } from 'zustand';
import type { MotionPreference, ThemePreference } from '@moods/ui';

const KEY = 'moods.prefs';

interface Persisted {
  theme: ThemePreference;
  motion: MotionPreference;
}

function load(): Persisted {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) return { theme: 'midnight', motion: 'system', ...(JSON.parse(raw) as Partial<Persisted>) };
  } catch {
    // storage unavailable (private mode)
  }
  return { theme: 'midnight', motion: 'system' };
}

function save(p: Persisted) {
  try {
    localStorage.setItem(KEY, JSON.stringify(p));
  } catch {
    // ignore
  }
}

interface PrefsState extends Persisted {
  setTheme: (t: ThemePreference) => void;
  setMotion: (m: MotionPreference) => void;
}

export const usePrefs = create<PrefsState>((set, get) => ({
  ...load(),
  setTheme: theme => {
    set({ theme });
    save({ theme, motion: get().motion });
  },
  setMotion: motion => {
    set({ motion });
    save({ theme: get().theme, motion });
  },
}));
